import {
  useTranslate,
  IResourceComponentsProps,
} from '@pankod/refine-core';
import {
  List,
  Table,
  TextField,
  useTable,
  getDefaultFilter,
  Space,
  EditButton,
  DeleteButton,
  TagField,
  ShowButton,
  FilterDropdown,
  Radio,
} from '@pankod/refine-antd';
import { User } from 'interfaces';
import { UserRoleEnum } from 'types/users/userRole';
import { EnumToArray } from 'util/parseData';
import { getColumnSearchProps } from 'components/data/customFunc';
import { EditOutlined, EyeOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';

const tagColors: Record<string, string> = {
  admin: '#f56a00',
  user: '#7265e6',
  guest: '#00a2ae',
  fr: '#1890ff',
  en: '#52c41a',
  es: '#faad14',
};

export const UserList: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const { tableProps, filters, tableQueryResult } = useTable<User>({
    initialSorter: [
      {
        field: 'createdAt',
        order: 'desc',
      },
    ],
    initialPageSize: 10,
  });

  return (
    <List>
      <style>{`
        .user-list-table {
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          padding: 4px;
          overflow-x: auto;
          font-family: "Segoe UI", Arial, sans-serif;
        }
        .user-list-table .ant-table {
          min-width: 720px;
          border: none;
        }
        .user-list-table .ant-table-thead > tr > th {
          background-color: #1890ff !important;
          color: #ffffff !important;
          font-weight: 600;
          border-bottom: none;
          padding: 12px 16px;
          transition: none;
        }
        .user-list-table .ant-table-thead > tr > th:hover {
          background-color: #1890ff !important;
          color: #ffffff !important;
        }
        .user-list-table .ant-table-tbody > tr:hover {
          background-color: #f5f7fa;
        }
        .user-list-table .ant-table-tbody > tr > td {
          padding: 12px 16px;
          color: #333;
          font-weight: 500;
        }
        .action-buttons > * {
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: background-color 0.3s ease;
          color: #555;
        }
        .action-buttons > *:hover {
          background-color: #e6f7ff;
          color: #1890ff;
        }
        .ant-tag {
          border-radius: 16px;
          font-weight: 600;
          color: white !important;
          padding: 0 12px;
          height: 28px;
          line-height: 28px;
          text-transform: capitalize;
        }
        .ant-table-filter-trigger {
          display: inline-flex !important;
          visibility: visible !important;
          opacity: 1 !important;
          color: #bfbfbf !important;
        }
        @media (max-width: 768px) {
          .user-list-table .ant-table {
            min-width: 100%;
            font-size: 14px;
          }
          .action-buttons > * {
            width: 32px;
            height: 32px;
          }
        }
        @media (max-width: 480px) {
          .user-list-table .ant-table {
            font-size: 13px;
          }
          .action-buttons > * {
            width: 28px;
            height: 28px;
          }
        }
      `}</style>

      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: true }}
        className="user-list-table"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
        }}
      >
        <Table.Column
          dataIndex="id"
          key="id"
          title={t('fields.ID', { ns: 'users' })}
          render={(_, __, index) => {
            const pagination = tableProps.pagination;
            const page = pagination && typeof pagination !== 'boolean' ? pagination.current || 1 : 1;
            const pageSize = pagination && typeof pagination !== 'boolean' ? pagination.pageSize || 10 : 10;
            return <TextField value={(page - 1) * pageSize + index + 1} />;
          }}
          responsive={['md']}
          sorter
          filterIcon={() => <SearchOutlined style={{ color: '#bfbfbf' }} />}
        />
        <Table.Column
          dataIndex="lastname"
          key="lastname"
          title={t('fields.lastname', { ns: 'users' })}
          render={(value) => <TextField value={value} />}
          {...getColumnSearchProps('lastname', 'text')}
          sorter
          filterIcon={() => <SearchOutlined style={{ color: '#bfbfbf' }} />}
        />
        <Table.Column
          dataIndex="firstname"
          key="firstname"
          title={t('fields.firstname', { ns: 'users' })}
          render={(value) => <TextField value={value} />}
          {...getColumnSearchProps('firstname', 'text')}
          sorter
          filterIcon={() => <SearchOutlined style={{ color: '#bfbfbf' }} />}
        />
        <Table.Column
          dataIndex="email"
          key="email"
          title={t('fields.email', { ns: 'users' })}
          render={(value) => <TextField value={value} />}
          {...getColumnSearchProps('email', 'text')}
          responsive={['lg']}
          sorter
          filterIcon={() => <SearchOutlined style={{ color: '#bfbfbf' }} />}
        />
        <Table.Column
          dataIndex="phone"
          key="phone"
          title={t('fields.phone', { ns: 'users' })}
          render={(value) => <TextField value={value} />}
          {...getColumnSearchProps('phone', 'text')}
          responsive={['md']}
          sorter
          filterIcon={() => <SearchOutlined style={{ color: '#bfbfbf' }} />}
        />
        <Table.Column
          dataIndex="role"
          key="role"
          title={t('fields.role', { ns: 'users' })}
          render={(value) => (
            <TagField
              value={value}
              color={tagColors[value as string] || '#999'}
            />
          )}
          filterSearch
          filterDropdown={(props) => (
            <FilterDropdown {...props}>
              <Radio.Group>
                {EnumToArray(UserRoleEnum).map((role) => (
                  <Radio value={role.value} key={role.value}>
                    {role.label}
                  </Radio>
                ))}
              </Radio.Group>
            </FilterDropdown>
          )}
          defaultFilteredValue={getDefaultFilter('role', filters)}
        />
        <Table.Column
          dataIndex="language"
          key="language"
          title={t('fields.language', { ns: 'users' })}
          render={(value) => (
            <TagField
              value={value}
              color={tagColors[value as string] || '#999'}
            />
          )}
          responsive={['lg']}
        />
        <Table.Column<User>
          title={t('actions')}
          dataIndex="actions"
          fixed="right"
          width={120}
          render={(_, record) => (
            <Space size="middle" className="action-buttons">
              <EditButton
                hideText
                size="small"
                recordItemId={record.id}
                icon={<EditOutlined />}
                title={t('buttons.edit')}
              />
              <ShowButton
                hideText
                size="small"
                recordItemId={record.id}
                icon={<EyeOutlined />}
                title={t('buttons.show')}
              />
              <DeleteButton
                hideText
                size="small"
                recordItemId={record.id}
                icon={<DeleteOutlined />}
                title={t('buttons.delete')}
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};